import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/api';

const QuotationForm = ({
    formData,
    onInputChange,
    onAddOption,
    onRemoveOption,
    onAddInclusive,
    onRemoveInclusive,
    onAddExclusive,
    onRemoveExclusive,
    onSubmit,
    isGenerating,
    token,
    currentDraftId,
    setCurrentDraftId,
    onLoadDraft
}) => {
    const [drafts, setDrafts] = useState([]);
    const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
    const [showDraftsDialog, setShowDraftsDialog] = useState(false);

    // Load drafts on component mount
    useEffect(() => {
        loadDraftsList();
    }, []);

    // Load drafts from server
    const loadDraftsList = async () => {
        setIsLoadingDrafts(true);
        try {
            const response = await axios({
                method: 'get',
                url: `${API_BASE_URL}/quotation/drafts`,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setDrafts(response.data.drafts || []);
            } else {
                setDrafts([]);
            }
        } catch (error) {
            console.error('Error loading drafts:', error);
            setDrafts([]);
        } finally {
            setIsLoadingDrafts(false);
        }
    };

    // Save draft
    const saveDraft = async () => {
        if (!formData.client || formData.client.trim() === '') {
            alert('Please enter at least a client name to save draft');
            return;
        }

        const draftData = {
            draftId: currentDraftId || `DRAFT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            quoteNo: formData.quoteNo || 'N/A',
            date: formData.date || new Date().toISOString().split('T')[0],
            clientName: formData.client || '',
            clientPhone: formData.phone || '',
            clientLocation: formData.location || '',
            fromCompany: formData.from?.company || 'PRIMEDALE TOURS & TRAVEL',
            fromPhone: formData.from?.phone || '+254745715136',
            fromAddress: formData.from?.address || 'VIEWPARK TOWERS, UTALII LANE',
            footerEmail: formData.footer?.email || 'info@primedaletours.com',
            footerWebsite: formData.footer?.website || 'www.primedaletours.com',
            footerPhone: formData.footer?.phone || '+254745715136',
            footerTagline: formData.footer?.tagline || 'Precision in Every Journey',
            options: formData.options.map(opt => ({
                sl: opt.sl || '',
                name: opt.name || '',
                amount: parseFloat(opt.amount) || 0,
                description: opt.description || ''
            })),
            inclusives: formData.inclusives.filter(item => item && item.trim() !== ''),
            exclusives: formData.exclusives.filter(item => item && item.trim() !== '')
        };

        try {
            const response = await axios({
                method: 'post',
                url: `${API_BASE_URL}/quotation/draft/save`,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                data: JSON.stringify(draftData)
            });

            if (response.data.success) {
                setCurrentDraftId(response.data.draftId || draftData.draftId);
                alert('Draft saved successfully!');
                loadDraftsList();
            } else {
                alert(response.data.message || 'Failed to save draft');
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            alert(error.response?.data?.message || 'Failed to save draft');
        }
    };

    // Delete draft
    const deleteDraft = async (draftId) => {
        if (!draftId) {
            alert('Invalid draft ID');
            return;
        }

        if (!window.confirm('Delete this draft? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await axios({
                method: 'delete',
                url: `${API_BASE_URL}/quotation/draft/${draftId}`,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setDrafts(drafts.filter(d => d.draft_id !== draftId && d.draftId !== draftId));
                if (currentDraftId === draftId) {
                    setCurrentDraftId(null);
                }
                alert('Draft deleted successfully!');
            } else {
                alert(response.data.message || 'Failed to delete draft');
            }
        } catch (error) {
            console.error('Error deleting draft:', error);
            alert(error.response?.data?.message || 'Failed to delete draft');
        }
    };

    // Drafts Dialog Component
    const DraftsDialog = () => {
        if (!showDraftsDialog) return null;

        return (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}>
                <div className="modal-dialog modal-lg" style={{ marginTop: '50px' }}>
                    <div className="modal-content">
                        <div className="modal-header" style={{ backgroundColor: '#0B214B', color: 'white' }}>
                            <h5 className="modal-title">
                                <i className="fa fa-drafts me-2"></i>
                                Saved Drafts
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                onClick={() => setShowDraftsDialog(false)}
                            ></button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {isLoadingDrafts ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : drafts.length === 0 ? (
                                <div className="text-center py-4">
                                    <i className="fa fa-inbox fa-3x text-muted"></i>
                                    <p className="text-muted mt-2">No drafts found</p>
                                </div>
                            ) : (
                                drafts.map((draft, index) => {
                                    const draftId = draft.draft_id || draft.draftId || '';
                                    const clientName = draft.client_name || draft.clientName || 'Untitled Draft';
                                    const quoteNo = draft.quote_no || draft.quoteNo || 'N/A';
                                    const savedAt = draft.saved_at || draft.savedAt || null;
                                    const optionsCount = draft.options ? draft.options.length : 0;

                                    let formattedDate = 'Unknown date';
                                    if (savedAt) {
                                        try {
                                            const d = new Date(savedAt);
                                            formattedDate = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                                        } catch (e) {
                                            formattedDate = 'Invalid date';
                                        }
                                    }

                                    return (
                                        <div key={index} className="card mb-2">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <h6 className="mb-1">{clientName}</h6>
                                                        <small className="text-muted">
                                                            <i className="fa fa-hashtag me-1"></i>
                                                            {quoteNo}
                                                            <span className="mx-2">|</span>
                                                            <i className="fa fa-clock-o me-1"></i>
                                                            {formattedDate}
                                                            <span className="mx-2">|</span>
                                                            <i className="fa fa-list me-1"></i>
                                                            {optionsCount} options
                                                        </small>
                                                    </div>
                                                    <div>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            onClick={() => {
                                                                setShowDraftsDialog(false);
                                                                onLoadDraft(draft);
                                                            }}
                                                        >
                                                            <i className="fa fa-folder-open"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => deleteDraft(draftId)}
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowDraftsDialog(false)}
                            >
                                Close
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => { loadDraftsList(); }}
                                style={{ backgroundColor: '#FF831D', borderColor: '#FF831D' }}
                            >
                                <i className="fa fa-refresh me-1"></i>
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Drafts Dialog */}
            <DraftsDialog />

            <form onSubmit={onSubmit}>
                {/* Action Buttons - Draft Management */}
                <div className="mb-3 d-flex gap-2 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setShowDraftsDialog(true)}
                    >
                        <i className="fa fa-drafts me-1"></i>
                        Drafts
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-success"
                        onClick={saveDraft}
                    >
                        <i className="fa fa-save me-1"></i>
                        Save Draft
                    </button>
                    {currentDraftId && (
                        <span className="badge bg-warning d-flex align-items-center ms-2">
                            <i className="fa fa-edit me-1"></i>
                            Editing Draft: {currentDraftId.substring(0, 8)}...
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-2"
                                style={{ fontSize: '10px' }}
                                onClick={() => setCurrentDraftId(null)}
                            ></button>
                        </span>
                    )}
                </div>

                {/* Quotation Details Section */}
                <div className="card mb-4 shadow-sm">
                    <div className="card-header light-grey-bg text-white text-center">
                        <h4 className="mb-0">
                            <i className="fa fa-file-invoice me-2"></i>
                            Quotation Details
                        </h4>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Quote Number *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.quoteNo}
                                    onChange={(e) => onInputChange('', 'quoteNo', e.target.value)}
                                    required
                                    placeholder="e.g., QPD1126"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Date *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.date}
                                    onChange={(e) => onInputChange('', 'date', e.target.value)}
                                    required
                                    placeholder="DD/MM/YYYY"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Client Name *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.client}
                                    onChange={(e) => onInputChange('', 'client', e.target.value)}
                                    required
                                    placeholder="John Doe"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Client Phone *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.phone}
                                    onChange={(e) => onInputChange('', 'phone', e.target.value)}
                                    required
                                    placeholder="+254704760532"
                                />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-bold">Client Location</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.location}
                                    onChange={(e) => onInputChange('', 'location', e.target.value)}
                                    placeholder="NAIROBI"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Options Section */}
                <div className="card mb-4 shadow-sm">
                    <div className="card-header light-grey-bg text-white text-center">
                        <h4 className="mb-0">
                            <i className="fa fa-tag me-2"></i>
                            Service Options / Packages
                        </h4>
                    </div>
                    <div className="card-body">
                        {formData.options.map((option, index) => (
                            <div key={index} className="option-card p-3 mb-3 border rounded">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0 text-primary">Option {index + 1}</h5>
                                    {formData.options.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => onRemoveOption(index)}
                                            className="btn btn-sm btn-danger"
                                        >
                                            <i className="fa fa-trash me-1"></i>Remove
                                        </button>
                                    )}
                                </div>
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">SL *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={option.sl}
                                            onChange={(e) => onInputChange('options', 'sl', e.target.value, index)}
                                            required
                                            placeholder="e.g., car hire"
                                        />
                                    </div>
                                </div>
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Package Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={option.name}
                                            onChange={(e) => onInputChange('options', 'name', e.target.value, index)}
                                            required
                                            placeholder="e.g., Lazarus Lodge"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Amount (KES) *</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={option.amount}
                                            onChange={(e) => onInputChange('options', 'amount', e.target.value, index)}
                                            required
                                            placeholder="21000"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Description</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={option.description}
                                            onChange={(e) => onInputChange('options', 'description', e.target.value, index)}
                                            placeholder="Per person sharing"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button type="button" onClick={onAddOption} className="btn btn-primary">
                            <i className="fa fa-plus me-2"></i>Add Another Option
                        </button>
                    </div>
                </div>

                {/* Inclusives Section */}
                <div className="card mb-4 shadow-sm">
                    <div className="card-header light-grey-bg text-white text-center">
                        <h4 className="mb-0">
                            <i className="fa fa-check-circle me-2"></i>
                            Inclusives (What's Included)
                        </h4>
                    </div>
                    <div className="card-body">
                        {formData.inclusives.map((inclusive, index) => (
                            <div key={index} className="input-group mb-2">
                                <span className="input-group-text light-grey-bg text-white">
                                    <i className="fa fa-check"></i>
                                </span>
                                <textarea
                                    className="form-control"
                                    value={inclusive}
                                    onChange={(e) => onInputChange('inclusives', '', e.target.value, index)}
                                    placeholder="e.g., 2 Nights accommodation on half board in a standard double room"
                                    rows="2"
                                />
                                {formData.inclusives.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => onRemoveInclusive(index)}
                                        className="btn btn-danger"
                                    >
                                        <i className="fa fa-times"></i>
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={onAddInclusive} className="btn btn-outline-primary mt-2">
                            <i className="fa fa-plus me-2"></i>Add Item
                        </button>
                    </div>
                </div>

                {/* Exclusives Section */}
                <div className="card mb-4 shadow-sm">
                    <div className="card-header light-grey-bg text-white text-center">
                        <h4 className="mb-0">
                            <i className="fa fa-times-circle me-2"></i>
                            Exclusives (What's Not Included)
                        </h4>
                    </div>
                    <div className="card-body">
                        {formData.exclusives.map((exclusive, index) => (
                            <div key={index} className="input-group mb-2">
                                <span className="input-group-text bg-danger text-white">
                                    <i className="fa fa-times"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={exclusive}
                                    onChange={(e) => onInputChange('exclusives', '', e.target.value, index)}
                                    placeholder="e.g., Transport"
                                />
                                {formData.exclusives.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => onRemoveExclusive(index)}
                                        className="btn btn-danger"
                                    >
                                        <i className="fa fa-times"></i>
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={onAddExclusive} className="btn btn-outline-primary mt-2">
                            <i className="fa fa-plus me-2"></i>Add Item
                        </button>
                    </div>
                </div>

                {/* Hidden Fields */}
                <input type="hidden" name="from_company" value={formData.from.company} />
                <input type="hidden" name="from_phone" value={formData.from.phone} />
                <input type="hidden" name="from_address" value={formData.from.address} />
                <input type="hidden" name="to_name" value={formData.client} />
                <input type="hidden" name="to_phone" value={formData.phone} />
                <input type="hidden" name="to_location" value={formData.location} />
                <input type="hidden" name="footer_email" value={formData.footer.email} />
                <input type="hidden" name="footer_website" value={formData.footer.website} />
                <input type="hidden" name="footer_phone" value={formData.footer.phone} />
                <input type="hidden" name="footer_tagline" value={formData.footer.tagline} />

                {/* Form Actions */}
                <div className="d-flex justify-content-center gap-3 mb-5 flex-wrap">
                    <button
                        type="submit"
                        className="btn btn-primary btn-lg px-5"
                        disabled={isGenerating}
                        style={{ backgroundColor: '#FF831D', borderColor: '#FF831D' }}
                    >
                        {isGenerating ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Generating PDF...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-file-pdf me-2"></i>
                                Generate Quotation PDF
                            </>
                        )}
                    </button>
                </div>
            </form>
        </>
    );
};

export default QuotationForm;